import { Theme } from "@mui/material";
import { ScoreImprovement } from "src/types";
import { formatNumber } from "src/utils/formatting";

const CALCULATION_NOISE_EXTRA_DECIMALS = 4;

export type ImpactDisplay = {
    displayedValue: number;
    isApproximate: boolean;
};

export const getImpactDisplay = (value: number, precision: number): ImpactDisplay => {
    const magnitude = Math.abs(value);

    if (magnitude < 10 ** -(precision + CALCULATION_NOISE_EXTRA_DECIMALS)) {
        return { displayedValue: 0, isApproximate: false };
    }
    if (magnitude < 0.5 * 10 ** -precision) {
        return { displayedValue: 0, isApproximate: true };
    }

    return { displayedValue: value, isApproximate: false };
};

export const getScoreImpactFormatOptions = (precision: number): Intl.NumberFormatOptions => ({
    minimumFractionDigits: precision,
    signDisplay: "exceptZero",
});

export const formatImpact = (
    value: number,
    precision: number,
    locale: string,
    options: Intl.NumberFormatOptions = {},
): string => {
    const { displayedValue, isApproximate } = getImpactDisplay(value, precision);
    const formatted = formatNumber(displayedValue, locale, {
        maximumFractionDigits: precision,
        ...options,
        withPrecisionFloor: false,
    });

    return isApproximate ? `≈\u00A0${formatted}` : formatted;
};

export const formatExactImpact = (value: number, locale: string): string => {
    return formatNumber(value, locale, { maximumSignificantDigits: 2, withPrecisionFloor: false });
};

export const formatImpactWithExactValue = (
    value: number,
    precision: number,
    locale: string,
    unit: string,
    options: Intl.NumberFormatOptions = {},
): string => {
    const formatted = formatImpact(value, precision, locale, options);
    if (getImpactDisplay(value, precision).isApproximate === false) {
        return formatted;
    }

    return `${formatted} (${formatExactImpact(value, locale)} ${unit})`;
};

export const getImpactColor = (
    value: number,
    precision: number,
    improvement: ScoreImprovement,
    theme: Theme,
): string | undefined => {
    const { displayedValue, isApproximate } = getImpactDisplay(value, precision);

    if (isApproximate) {
        return theme.custom.grey[2500];
    }
    if (displayedValue === 0) {
        return undefined;
    }

    const improves = improvement === "increase" ? displayedValue > 0 : displayedValue < 0;
    return improves ? theme.custom.green[700] : theme.custom.red[600];
};
